import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { resetFilters as resetDashboardFilters, saveFilters } from 'actions/dashboard';
import {
  resetFilters as resetDashboardPhoneFilters,
  saveFilters as saveDashboardPhoneFilters
} from 'actions/dashboardPhone';
import { resetFilters as resetMapFilters, searchDig, searchInventory } from 'actions/map';
import clsx from 'clsx';
import { AdvancedSearch } from 'common/components/AdvancedSearch/AdvancedSearch';
import { globalStyles } from 'GlobalStyles';
import { useDeepCompareEffect, useShowAlert } from 'hooks';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import { UserProviderContext } from 'providers/UserProvider';
import { filtersToUi } from 'selectors/app';
import strings from 'strings';
import CustomDatePicker from 'UI/components/atoms/CustomDatePicker';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPHint from 'UI/components/atoms/FPHint';
import InputRange from 'UI/components/atoms/InputRange';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import Searchbar from 'UI/components/molecules/Searchbar';
import { DateFormats, FilterRangeSufix, FilterType, HideMode } from 'UI/constants/defaults';
import { nestTernary } from 'UI/utils';

import { AllFiltersDefinition as DefaultFiltersDefinition } from './filters';
import { normalizeDependentFilters, validateFilters } from './filters.utils';
import { useStyles } from './styles';

/** @typedef {import('types/app').Filters} Filters */
/** @typedef {import('types/app').FiltersSection} FiltersSection */
/** @typedef {import('./filters').FilterDefinition} FilterDefinition */
/**
 * @typedef {Object} FilterGroup
 * @property {string} id
 * @property {string} name
 * @property {string[]} filters
 */
const chainedSelects = {
  coach: ['recruiter'],
  entityType: ['status', 'type'],
  industry: ['specialty', 'subspecialty', 'position'],
  specialty: ['subspecialty']
};

const mapSections = ['dig', 'inventory'];
const DEFAULT_VERSION = 1;

const getSelectedFilterValue = (filters, filterDef) =>
  !isNil(filters[filterDef.name])
    ? filters[filterDef.name]
    : nestTernary(filterDef.multiple, [], null);

const MapTabs = {
  Pil: 0,
  Inventory: 1
};

/**
 * @param {Object} props
 * @param {boolean} [props.areFiltersRequired=true]
 * @param {React.ReactNode} [props.children]
 * @param {*} [props.contentClassName]
 * @param {Filters} props.defaultFilters
 * @param {React.ReactNode} [props.emptyContent]
 * @param {boolean} [props.enableCollapsibleForm=false]
 * @param {FilterDefinition[]} [props.extraFiltersDefinition]
 * @param {FilterGroup[]} [props.groups]
 * @param {boolean} [props.hasLoaded]
 * @param {string[]} [props.includeFilters]
 * @param {Filters} props.initialFilters
 * @param {*} props.inventoryMapFilters
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} props.isMapInventoryTab
 * @param {React.ReactNode} [props.notificationBanner]
 * @param {Function} props.onFiltersReset
 * @param {Function} props.onSearch
 * @param {string} [props.searchButtonText='Search']
 * @param {FiltersSection} props.section
 * @param {boolean} [props.showGoToTop=false]
 */
export const SideFiltersLayout = ({
  areFiltersRequired,
  children,
  contentClassName,
  defaultFilters,
  emptyContent,
  enableCollapsibleForm,
  extraFiltersDefinition,
  groups,
  hasLoaded,
  includeFilters,
  initialFilters,
  inventoryMapFilters,
  isLoading,
  isMapInventoryTab,
  notificationBanner,
  onFiltersReset,
  onSearch,
  searchButtonText,
  section,
  showGoToTop
}) => {
  const { showAlert } = useShowAlert();
  const classes = useStyles();
  const [user] = useContext(UserProviderContext);
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [errors, setErrors] = useState(null);
  const [areFiltersDirty, setAreFiltersDirty] = useState(false);
  const AllFiltersDefinition =
    extraFiltersDefinition && Array.isArray(extraFiltersDefinition)
      ? DefaultFiltersDefinition.concat(extraFiltersDefinition)
      : DefaultFiltersDefinition;

  const filtersToInclude =
    groups && groups.length ? flatten(groups.map(group => group.filters)) : includeFilters;

  const filtersDefinition = filtersToInclude.map(filterToShow =>
    AllFiltersDefinition.find(filter => filter.name === filterToShow)
  );

  const topRef = useRef(null);

  const handleGoToTop = () => {
    topRef.current && (topRef.current.scrollTop = 0);
  };

  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  useEffect(() => {
    if (isLoading) setIsFormCollapsed(true);
  }, [isLoading]);

  useDeepCompareEffect(() => {
    setFilters({ ...defaultFilters, ...initialFilters });
  }, [initialFilters, defaultFilters]);

  const handleCheckToggle = event => {
    const { checked, name, title } = event.target;
    handleFilterChange(name, checked ? title : '');
  };

  const handleFilterChange = useCallback(
    (name, value) => {
      const wasFilterRemoved = isNil(value) || (Array.isArray(value) && value.length === 0);
      const newFilters = { ...filters };

      const filterDef = filtersDefinition.find(each => each.name === name);
      const dependents = normalizeDependentFilters(filterDef?.dependent);

      if (wasFilterRemoved) {
        delete newFilters[name];

        dependents.forEach(dependentFilterName => {
          delete newFilters[dependentFilterName];
        });
      } else {
        newFilters[name] = value;

        dependents.forEach(dependentFilterName => {
          const dependentFilterDef = filtersDefinition.find(
            each => each.name === dependentFilterName
          );

          const isVisible =
            dependentFilterDef?.showWhen !== undefined
              ? dependentFilterDef.showWhen(newFilters, user)
              : true;

          !isVisible && delete newFilters[dependentFilterName];
        });
      }

      if (name && chainedSelects[name]) {
        chainedSelects[name].forEach(chainedSelect => {
          newFilters[chainedSelect] = null;
        });
      }

      const validationErrors = validateFilters(newFilters, filtersDefinition);
      setErrors(validationErrors);

      setFilters(newFilters);
      setAreFiltersDirty(true);
    },
    [filters, filtersDefinition, user]
  );

  const handleAdvancedSearchSelectionChange = useCallback(
    filterName => selectedItems => {
      handleFilterChange(filterName, selectedItems);
    },
    [handleFilterChange]
  );

  const handleDateChange =
    (format = DateFormats.QueryShortFormat) =>
    (name, value) => {
      setFilters(prevState => ({
        ...prevState,
        [name]: value?.format(format) ?? value
      }));
    };

  const renderAlert = () => {
    const bodyTxt = isMapInventoryTab ? strings.map.inventory.alertBody : strings.map.dig.alertBody;
    showAlert({
      severity: 'warning',
      title: strings.map.inventory.alertTitle,
      body: bodyTxt
    });
  };

  const invalidFields = filtersDefinition.filter(
    filter => filter.isValid && !filter.isValid(filters[filter.name])
  );

  const areInvalidFields = invalidFields.length > 0;

  const handleSearchClick = async () => {
    const validationErrors = validateFilters(filters, filtersDefinition);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showAlert({
        severity: 'error',
        title: strings.shared.validations.title,
        body: strings.shared.fields.requiredAlert
      });
      return;
    }

    if (isMapInventoryTab && atLeastTwoFilters()) {
      renderAlert();
      return;
    }

    if (!atLeastOneFilter && areFiltersRequired) {
      renderAlert();
      return;
    }

    const asyncTaskToSearch = async () => {
      const filtersWithValue = {};

      if (areInvalidFields) return;

      filtersDefinition.forEach(eachFilter => {
        const isFilterRangeType = eachFilter.type === FilterType.Range;

        if (isFilterRangeType) {
          const filterValueFrom = filters[`${eachFilter.name}${FilterRangeSufix.From}`];
          if (!isNil(filterValueFrom) && filterValueFrom !== '') {
            filtersWithValue[`${eachFilter.name}${FilterRangeSufix.From}`] = {
              paramName: `${eachFilter.paramName}${FilterRangeSufix.From}`,
              value: filterValueFrom,
              displayLabel: `${eachFilter?.displayLabel ?? ''} ${FilterRangeSufix.From}`,
              requiredWith: eachFilter?.requiredWith
            };
          }

          const filterValueTo = filters[`${eachFilter.name}${FilterRangeSufix.To}`];
          if (!isNil(filterValueTo) && filterValueTo !== '') {
            filtersWithValue[`${eachFilter.name}${FilterRangeSufix.To}`] = {
              paramName: `${eachFilter.paramName}${FilterRangeSufix.To}`,
              value: filterValueTo,
              displayLabel: `${eachFilter?.displayLabel ?? ''} ${FilterRangeSufix.To}`,
              requiredWith: eachFilter?.requiredWith
            };
          }
        } else {
          const filterValue = filters[eachFilter.name];
          if (!isNil(filterValue) && filterValue !== '') {
            filtersWithValue[eachFilter.name] = {
              paramName: eachFilter.paramName,
              value: filters[eachFilter.name],
              idKey: eachFilter.idKey,
              outboundValue: eachFilter.outboundTransformer
                ? eachFilter.outboundTransformer(filters[eachFilter.name])
                : null,
              displayLabel: eachFilter?.displayLabel,
              requiredWith: eachFilter?.requiredWith
            };
          }
        }
      });

      onSearch && onSearch(section, filtersWithValue);
      setAreFiltersDirty(false);
    };
    await asyncTaskToSearch();
  };

  const handleResetClick = () => {
    onFiltersReset && onFiltersReset(section);
    setFilters(defaultFilters);
    setErrors(null);
    setIsFormCollapsed(false);
  };

  const renderFilter = filterDef => {
    const isVisible = filterDef.showWhen !== undefined ? filterDef.showWhen(filters, user) : true;

    const title =
      typeof filterDef.title === 'string'
        ? filterDef.title
        : nestTernary(typeof filterDef.title === 'function', filterDef.title(filters, section), '');

    if (!isVisible && (!filterDef.hideMode || filterDef.hideMode === HideMode.Unmounted)) {
      return null;
    }

    const filterStyles = {
      ...(filterDef.customStyle ? filterDef.customStyle : globalStyles.inputMinSpacing),
      display: !isVisible && filterDef.hideMode === HideMode.Hidden ? 'none' : 'block'
    };

    const isInvalid =
      areInvalidFields && filterDef.isValid && !filterDef.isValid(filters[filterDef.name]);

    switch (filterDef.type) {
      case FilterType.Autocomplete:
        return (
          <div key={`filter-${filterDef.name}`} style={filterStyles}>
            <AutocompleteSelect
              api={filterDef?.api}
              apiVersion={filterDef?.apiVersion || DEFAULT_VERSION}
              name={filterDef.name}
              selectedValue={getSelectedFilterValue(filters, filterDef)}
              label={title}
              placeholder={`${
                filterDef.placeholder || `${strings.shared.filters.filterBy} ${title}`
              }`}
              displayKey={filterDef.displayKey || 'title'}
              url={
                typeof filterDef.url === 'string'
                  ? filterDef.url
                  : nestTernary(
                      typeof filterDef.url === 'function',
                      filterDef.url(filters, section, user),
                      ''
                    )
              }
              onSelect={handleFilterChange}
              renderOption={filterDef.renderOption || undefined}
              disableClearable={filterDef.disableClearable}
              startAdornment={
                filters[filterDef.name] &&
                filterDef.startAdornment &&
                filterDef.startAdornment(
                  filters[filterDef.name].style_class_name ||
                    filters[filterDef.name].style ||
                    filters[filterDef.name].color
                )
              }
              autocomplete="nothing"
              error={!!errors?.[filterDef.name]}
              errorText={errors?.[filterDef.name]?.message}
              getOptionLabel={filterDef.getOptionLabel}
              getOptionSelected={filterDef.getOptionSelected}
              groupBy={filterDef.groupBy}
              multiple={filterDef.multiple}
              typeahead={filterDef.typeahead}
              typeaheadLimit={filterDef.typeaheadLimit}
              typeaheadParams={
                typeof filterDef.typeaheadParams === 'function'
                  ? filterDef.typeaheadParams(filters, section, user)
                  : filterDef.typeaheadParams
              }
            />
          </div>
        );
      case FilterType.Search:
        return (
          <Box key={`filter-${filterDef.name}`} style={filterStyles}>
            <Searchbar
              name={filterDef.name}
              placeholder={title}
              value={filters[filterDef.name] || ''}
              width="100%"
              onChange={handleFilterChange}
              onSearch={handleSearchClick}
            />
          </Box>
        );
      case FilterType.Select:
        return (
          <Box key={`filter-${filterDef.name}`} style={filterStyles}>
            <AutocompleteSelect
              disableClearable={filterDef.disableClearable}
              displayKey={filterDef.displayKey || 'title'}
              error={!!errors?.[filterDef.name]}
              errorText={errors?.[filterDef.name]?.message}
              groupBy={filterDef.groupBy}
              getOptionSelected={filterDef?.getOptionSelected}
              multiple={filterDef?.multiple}
              name={filterDef.name}
              onSelect={handleFilterChange}
              options={filterDef.options}
              placeholder={filterDef.placeholder || filterDef.title}
              renderOption={filterDef.renderOption || undefined}
              selectedValue={getSelectedFilterValue(filters, filterDef)}
              width="100%"
            />
          </Box>
        );
      case FilterType.Input:
        return (
          <Box key={`filter-${filterDef.name}`} style={filterStyles}>
            <TextBox
              error={isInvalid}
              errorText={isInvalid ? filterDef.errorText : null}
              inputProps={filterDef?.InputProps}
              inputType={filterDef.inputType}
              label={filterDef.title}
              name={filterDef.name}
              onChange={handleFilterChange}
              placeholder={filterDef.placeholder}
              value={filters[filterDef.name] || ''}
            />
          </Box>
        );
      case FilterType.Switch:
        return (
          <Box key={`filter-${filterDef.name}`} style={filterStyles}>
            <FormControlLabel
              label={filterDef.title}
              control={
                <Checkbox
                  checked={filters[filterDef.name] === filterDef.title}
                  color="primary"
                  name={filterDef.name}
                  onChange={handleCheckToggle}
                  title={filterDef.title}
                  value={filters[filterDef.name]}
                  inputProps={{ title: filterDef.title }}
                />
              }
            />
          </Box>
        );
      case FilterType.Range:
        return (
          <InputRange
            inputType={filterDef.inputType}
            key={`filter-${filterDef.name}`}
            label={filterDef.title}
            name={filterDef.name}
            onChange={handleFilterChange}
            valueFrom={filters[`${filterDef.name}From`]}
            valueTo={filters[`${filterDef.name}To`]}
          />
        );
      case FilterType.Date:
        return (
          <CustomDatePicker
            key={`filter-${filterDef.name}`}
            label={filterDef.title}
            name={filterDef.name}
            onDateChange={handleDateChange(filterDef.format)}
            value={filters[filterDef.name] || null}
          />
        );
      case FilterType.AdvancedSearch:
        return (
          <div key={`filter-${filterDef.name}`} style={filterStyles}>
            <AdvancedSearch
              defaultPreference={filterDef.defaultPreference}
              defaultPriority={filterDef.defaultPriority}
              emptyText={filterDef.emptyText}
              fetchOptions={filterDef.fetchOptions}
              getOptionId={filterDef.getOptionId}
              getOptionLabel={filterDef.getOptionLabel}
              groupBy={filterDef.groupBy}
              noOptionsText={filterDef.noOptionsText}
              onSelectionChange={handleAdvancedSearchSelectionChange(filterDef.name)}
              options={filterDef.options}
              placeholder={filterDef.placeholder}
              preferences={filterDef.preferences}
              title={filterDef.title}
              typeahead={filterDef.typeahead}
              typeaheadLimit={filterDef.typeaheadLimit}
              typeaheadParams={filterDef.typeaheadParams}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderForm = () => (
    <section>
      {groups && groups.length > 0
        ? renderGroups()
        : filtersDefinition.map(eachFilter => renderFilter(eachFilter))}
    </section>
  );
  const renderGroups = () =>
    groups.map(({ id: groupId, name, filters: groupFilters, showWhen }) => {
      const isMapInventory = name === 'Location' && isMapInventoryTab && inventoryMapFilters.area;
      const isVisible = showWhen ? showWhen(filters, user) : true;

      if (!isVisible) return null;

      return (
        <section key={groupId} className={classes.group}>
          <Typography variant="subtitle1">{name}</Typography>
          {isMapInventory && <FPHint description={strings.map.inventory.locationHint} size="md" />}
          {groupFilters.map(eachFilter =>
            renderFilter(AllFiltersDefinition.find(filter => filter.name === eachFilter))
          )}
        </section>
      );
    });

  const renderTopBar = () => (
    <div className={clsx(classes.topBarContainer, classes.topBarShadow)}>
      <div className={clsx(classes.topBar)}>
        {enableCollapsibleForm && (
          <Button
            className={classes.hideShowButton}
            onClick={toggleCollapse}
            endIcon={<ListAltIcon style={{ fontSize: 20 }} />}
            disabled={!hasLoaded}
          >
            {isFormCollapsed ? strings.shared.ui.showFilters : strings.shared.ui.hideFilters}
          </Button>
        )}

        <Button
          color="primary"
          size="small"
          onClick={handleResetClick}
          disabled={!atLeastOneFilter && areFiltersRequired}
          className={
            !atLeastOneFilter && areFiltersRequired ? classes.resetButton : classes.resetActive
          }
        >
          {strings.shared.ui.resetAll}
        </Button>
      </div>
    </div>
  );

  const atLeastOneFilter = filtersDefinition.some(eachFilter => filters[eachFilter.name]);
  const isUsaSelected = filters?.countries?.some(eachValue => eachValue.id === 1);
  const atLeastTwoFilters = () => {
    const minFiltersRequired = isUsaSelected ? 2 : 1;
    const arrayOfFilters = Object.values(filters).filter(eachFilter => Array.isArray(eachFilter));
    const arrayOfFiltersWithValues = arrayOfFilters.filter(eachFilter => eachFilter.length > 0);

    return arrayOfFiltersWithValues.length < minFiltersRequired;
  };

  const shouldCollapseForm = isFormCollapsed;
  const toggleCollapse = () => {
    if (isFormCollapsed) {
      handleGoToTop();
    }
    setIsFormCollapsed(!isFormCollapsed);
  };

  return (
    <div className={clsx(classes.container, contentClassName)}>
      {renderTopBar()}
      <div className={classes.filtersContent} ref={topRef}>
        <When condition={!!notificationBanner}>
          <div className={classes.notificationBanner}>{notificationBanner}</div>
        </When>
        <div className={clsx(shouldCollapseForm ? classes.hiddenForm : classes.shownForm)}>
          {renderForm()}
        </div>
        {!areFiltersDirty && atLeastOneFilter && emptyContent && isFormCollapsed && (
          <>{emptyContent}</>
        )}
        {isLoading && (
          <div className={classes.circularProgressFormContainer}>
            <CircularProgress />
          </div>
        )}
        {children}
        {showGoToTop && (
          <Tooltip title={strings.shared.ui.backToTop} placement="top">
            <Button color="primary" className={classes.goToTopButton} onClick={handleGoToTop}>
              <ExpandLessIcon />
            </Button>
          </Tooltip>
        )}
      </div>
      <Box className={classes.filterButton}>
        <FPActionButton
          style={globalStyles.mapActionButton}
          text={isLoading ? strings.shared.ui.searching : searchButtonText}
          onClick={handleSearchClick}
          iconPosition="right"
          disabled={isLoading}
        >
          {isLoading && <CircularProgress className={classes.circularProgress} size={24} />}
        </FPActionButton>
      </Box>
    </div>
  );
};

SideFiltersLayout.defaultProps = {
  searchButtonText: strings.shared.ui.search,
  areFiltersRequired: true,
  enableCollapsibleForm: false,
  isLoading: false,
  groups: [],
  includeFilters: [],
  showGoToTop: false,
  contentClassName: null,
  children: undefined
};

const mapStateToProps = (state, ownProps) => {
  const { section } = ownProps;
  const slice = mapSections.includes(section) ? 'map' : section;
  return {
    isLoading: state[slice]?.ui.isLoading,
    hasLoaded: state[slice]?.ui.hasLoaded,
    initialFilters: filtersToUi(state[slice]?.domain.filters),
    isMapInventoryTab: state.map.ui.activeTab === MapTabs.Inventory,
    inventoryMapFilters: state.map.domain.filters
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onSearch: (section, filters) => dispatch(determineOnSearch(section, filters)),
    onFiltersReset: section => dispatch(determineOnReset(section))
  };
};

/**
 * @param {FiltersSection} section
 * @param {Filters} filters
 */
const determineOnSearch = (section, filters) => {
  switch (section) {
    case 'dig':
      return searchDig(filters);
    case 'inventory':
      return searchInventory(filters);
    case 'dashboardPhone':
      return saveDashboardPhoneFilters(filters);
    default:
      return saveFilters(filters);
  }
};

/**
 * @param {FiltersSection} section
 */
const determineOnReset = section => {
  switch (section) {
    case 'dashboard':
      return resetDashboardFilters();
    case 'dashboardPhone':
      return resetDashboardPhoneFilters();
    default:
      return resetMapFilters();
  }
};

const SideFiltersLayoutConnected = connect(mapStateToProps, mapDispatchToProps)(SideFiltersLayout);

export default SideFiltersLayoutConnected;
